import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { MONEY_PAGE_CSS, MONEY, fmtGBP as moneyFmt } from './moneyStyles';

/* ── UK tax constants (2025-26) ───────────────────────────── */
const PERSONAL_ALLOWANCE = 12_570;
const BASIC_RATE_LIMIT   = 50_270;
const HIGHER_RATE_LIMIT  = 125_140;

function calcTax(profit: number) {
  if (profit <= PERSONAL_ALLOWANCE) return { basic: 0, higher: 0, additional: 0, total: 0 };
  const taxableBasic      = Math.min(Math.max(0, profit - PERSONAL_ALLOWANCE), BASIC_RATE_LIMIT - PERSONAL_ALLOWANCE);
  const taxableHigher     = Math.min(Math.max(0, profit - BASIC_RATE_LIMIT), HIGHER_RATE_LIMIT - BASIC_RATE_LIMIT);
  const taxableAdditional = Math.max(0, profit - HIGHER_RATE_LIMIT);
  const basic      = taxableBasic * 0.20;
  const higher     = taxableHigher * 0.40;
  const additional = taxableAdditional * 0.45;
  return { basic, higher, additional, total: basic + higher + additional };
}

type FreqView = 'monthly' | 'quarterly' | 'annual';

const AC_EXTRA_CSS = `
  .ac-form { background: #fff; border: 2px solid ${MONEY.black}; border-radius: 14px; padding: 22px; margin-bottom: 24px; }
  .ac-form-title { font-size: 15px; font-weight: 800; color: ${MONEY.black}; margin: 0 0 16px; }
  .ac-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; }
  .ac-grid .span-all { grid-column: 1 / -1; }
  .field-label { display: block; font-size: 12px; font-weight: 700; color: #44403c; margin-bottom: 5px; }
  .field-input { width: 100%; padding: 9px 12px; border: 1.5px solid ${MONEY.border}; border-radius: 9px; font-size: 13px; font-family: inherit; color: ${MONEY.black}; outline: none; }
  .field-input:focus { border-color: ${MONEY.black}; }
  .btn-save { background: ${MONEY.black}; color: #fff; border: none; border-radius: 9px; padding: 10px 22px; font-weight: 800; font-size: 13px; cursor: pointer; font-family: inherit; }
  .btn-save:disabled { opacity: .6; cursor: default; }
  .btn-cancel { background: #fff; color: #44403c; border: 1.5px solid ${MONEY.border}; border-radius: 9px; padding: 10px 18px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; }
  .btn-csv { background: #fff; color: ${MONEY.black}; border: 1.5px solid ${MONEY.black}; border-radius: 9px; padding: 8px 16px; font-weight: 700; font-size: 12px; cursor: pointer; font-family: inherit; }
  .btn-csv:hover { background: ${MONEY.cream}; }
  .ac-chart { display: flex; align-items: flex-end; gap: 4px; height: 110px; }
  .ac-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 2px; height: 100%; justify-content: flex-end; }
  .ac-bars { display: flex; align-items: flex-end; gap: 2px; width: 100%; height: 96px; }
  .ac-bar { flex: 1; border-radius: 3px 3px 0 0; min-height: 0; }
  .ac-lbl { font-size: 9px; color: ${MONEY.muted}; white-space: nowrap; }
  .toast-ok { background: #f0fdf4; color: #16a34a; border: 1.5px solid #bbf7d0; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
  .toast-err { background: #fff1f1; color: #dc2626; border: 1.5px solid #fecaca; border-radius: 10px; padding: 11px 16px; margin-bottom: 16px; font-weight: 700; font-size: 13px; }
  .tax-section { margin-bottom: 24px; }
  .tax-section-title { font-size: 11px; font-weight: 800; color: ${MONEY.muted}; text-transform: uppercase; letter-spacing: .08em; margin: 0 0 10px; padding-bottom: 8px; border-bottom: 2px solid ${MONEY.black}; }
  .tax-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-radius: 8px; gap: 16px; flex-wrap: wrap; }
  .tax-row:nth-child(odd) { background: ${MONEY.cream}; }
  .tax-row-label { font-size: 14px; color: #44403c; font-weight: 600; flex: 1; min-width: 200px; }
  .tax-row-value { font-size: 15px; font-weight: 800; font-family: ${MONEY.mono}; white-space: nowrap; }
  .tax-row.total { background: #fef3c7; border: 1.5px solid ${MONEY.gold}; border-radius: 10px; margin-top: 8px; }
  .tax-row.total .tax-row-label { color: ${MONEY.black}; font-weight: 800; }
  .tax-row.total .tax-row-value { color: ${MONEY.black}; font-size: 20px; }
  .tax-disclaimer { font-size: 12px; color: ${MONEY.muted}; background: ${MONEY.cream}; border-radius: 10px; padding: 14px 16px; margin-top: 16px; line-height: 1.7; border: 1.5px solid ${MONEY.border}; }
  .freq-btn { padding: 7px 16px; border-radius: 9px; border: 1.5px solid ${MONEY.border}; background: #fff; color: ${MONEY.muted}; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; }
  .freq-btn.active { background: ${MONEY.black}; border-color: ${MONEY.black}; color: #fff; }
  .yield-input { width: 120px; padding: 7px 10px; border: 1.5px solid ${MONEY.border}; border-radius: 8px; font-size: 13px; font-family: ${MONEY.mono}; outline: none; text-align: right; }
  .yield-input:focus { border-color: ${MONEY.black}; }
  .src-badge { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 5px; white-space: nowrap; }
  .prop-bar-bg { height: 6px; background: #f5f5f4; border-radius: 3px; overflow: hidden; }
  .prop-bar-fill { height: 100%; border-radius: 3px; }
  .ac-empty { text-align: center; padding: 48px 24px; background: #fff; border: 2px dashed ${MONEY.border}; border-radius: 14px; }
  .ac-empty .ei { font-size: 40px; margin-bottom: 12px; }
  .ac-empty h3 { font-size: 17px; font-weight: 800; color: ${MONEY.black}; margin: 0 0 8px; }
  .ac-empty p { color: ${MONEY.muted}; font-size: 14px; margin: 0; }
  .money-table tfoot td { padding: 12px 14px; background: ${MONEY.cream}; font-weight: 900; border-top: 2px solid ${MONEY.black}; }
`;

interface LedgerEntry {
  id: number;
  property_address: string;
  type: 'income' | 'expense';
  category: string;
  amount: number;
  date: string;
  description: string;
  reference_id: number;
  reference_type: string;
}

interface PerProperty {
  address: string;
  income: number;
  expenses: number;
  net: number;
}

interface TransactionData {
  income: LedgerEntry[];
  expenses: LedgerEntry[];
  capital_income?: LedgerEntry[];
  capital_expenses?: LedgerEntry[];
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
  totalCapitalIncome?: number;
  totalCapitalExpenses?: number;
  per_property: PerProperty[];
  tax_year: string;
}

const TAX_YEARS = ['2023-24', '2024-25', '2025-26', '2026-27'];
const EXPENSE_CATEGORIES = ['Maintenance', 'Insurance', 'Mortgage Interest', 'Utilities', 'Furniture / Equipment', 'Letting Agent Fees', 'Cleaning', 'Legal / Professional', 'Stamp Duty', 'Acquisition Costs', 'Disposal Costs', 'Other'];

function defaultTaxYear(): string {
  const now = new Date();
  return now.getMonth() >= 3
    ? `${now.getFullYear()}-${String(now.getFullYear() + 1).slice(2)}`
    : `${now.getFullYear() - 1}-${String(now.getFullYear()).slice(2)}`;
}

function taxYearLabel(ty: string): string {
  const y = parseInt(ty.split('-')[0]);
  return `${y}–${y + 1} (6 Apr ${y} – 5 Apr ${y + 1})`;
}

function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const fmtGBP = moneyFmt;

type ActiveTab = 'summary' | 'income' | 'expenses' | 'properties' | 'tax';

export default function LandlordAccounting() {
  const { token } = useAuth();
  const [taxYear, setTaxYear]           = useState(defaultTaxYear());
  const [data, setData]                 = useState<TransactionData | null>(null);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState<ActiveTab>('summary');
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [properties, setProperties]     = useState<string[]>([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseForm, setExpenseForm]   = useState({ property_address: '', category: 'Maintenance', amount: '', date: new Date().toISOString().slice(0, 10), description: '', mortgage_interest: '' });
  const [savingExpense, setSavingExpense] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<number | null>(null);
  const [freqView, setFreqView]         = useState<FreqView>('monthly');
  const [yieldInputs, setYieldInputs]   = useState<Record<string, string>>({});
  const [incomeFilter, setIncomeFilter] = useState('');
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvProperty, setCsvProperty] = useState('');
  const [importingCsv, setImportingCsv] = useState(false);
  const csvFileRef = React.useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const [txRes, propRes] = await Promise.all([
        fetch(`${API_URL}/landlord/transactions?tax_year=${taxYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/landlord/properties`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!txRes.ok) throw new Error(`HTTP ${txRes.status}`);
      const txData = await txRes.json();
      const propData = await propRes.json();
      setData(txData);
      setProperties((propData.properties || []).map((p: any) => p.address));
    } catch (e: any) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [token, taxYear]);

  useEffect(() => { load(); }, [load]);

  const addManualExpense = async () => {
    if (!expenseForm.property_address || !expenseForm.amount || !expenseForm.description.trim()) { setError('Property, amount and description are required'); return; }
    setSavingExpense(true); setError('');
    try {
      const payload: any = { ...expenseForm, amount: Number(expenseForm.amount), type: 'expense' };
      if (expenseForm.category === 'Mortgage Interest' && expenseForm.mortgage_interest) {
        payload.mortgage_interest = Number(expenseForm.mortgage_interest);
      }
      const url = editingExpenseId ? `${API_URL}/landlord/transactions/${editingExpenseId}` : `${API_URL}/landlord/transactions`;
      const r = await fetch(url, {
        method: editingExpenseId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!r.ok) { const d = await r.json(); setError(d.error || 'Failed to save'); return; }
      setSuccess(editingExpenseId ? 'Expense updated' : 'Expense saved'); setShowAddExpense(false); setEditingExpenseId(null);
      setExpenseForm({ property_address: '', category: 'Maintenance', amount: '', date: new Date().toISOString().slice(0, 10), description: '', mortgage_interest: '' });
      load(); setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to save expense'); }
    finally { setSavingExpense(false); }
  };

  const importBankCsv = async () => {
    if (!csvText.trim() || !csvProperty) { setError('Paste CSV data and select a property'); return; }
    setImportingCsv(true); setError('');
    try {
      const r = await fetch(`${API_URL}/landlord/transactions/import-csv`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ csv: csvText, property_address: csvProperty }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Import failed'); return; }
      setSuccess(d.message || `Imported ${d.imported} transactions`);
      setShowCsvImport(false); setCsvText('');
      load(); setTimeout(() => setSuccess(''), 4000);
    } catch { setError('Import failed'); }
    finally { setImportingCsv(false); }
  };

  const deleteExpense = async (id: number) => {
    if (!window.confirm('Delete this manual expense?')) return;
    await fetch(`${API_URL}/landlord/transactions/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    load();
  };

  const startEditExpense = (t: any) => {
    setEditingExpenseId(t.id);
    setExpenseForm({ property_address: t.property_address, category: t.category, amount: String(t.amount), date: t.date?.split('T')[0] || '', description: t.description || '', mortgage_interest: t.mortgage_interest ? String(t.mortgage_interest) : '' });
    setShowAddExpense(true);
  };

  /* ── Tax computation ──────────────────────────────────── */
  const mortgageInterest = data ? data.expenses.filter((t: any) => String(t.category || '').toLowerCase().includes('mortgage')).reduce((s: number, t: any) => s + Number(t.mortgage_interest || t.amount || 0), 0) : 0;
  const section24Credit = Math.round(mortgageInterest * 0.20 * 100) / 100;
  const taxableProfit = data ? Math.max(0, data.netIncome - section24Credit) : 0;
  const taxInfo = data ? calcTax(taxableProfit) : null;

  /* ── Monthly buckets (Apr → Mar, UK tax year) ─────────── */
  const yearStart = parseInt(taxYear.split('-')[0]);
  const allMonths = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(yearStart, 3 + i, 1);
    return d.toISOString().slice(0, 7);
  });
  const incomeByMonth: Record<string, number>  = {};
  const expenseByMonth: Record<string, number> = {};
  if (data) {
    data.income.forEach(t => { const m = t.date.slice(0, 7); incomeByMonth[m]  = (incomeByMonth[m]  || 0) + Number(t.amount); });
    data.expenses.forEach(t => { const m = t.date.slice(0, 7); expenseByMonth[m] = (expenseByMonth[m] || 0) + Number(t.amount); });
  }
  const maxBar = Math.max(...allMonths.map(m => Math.max(incomeByMonth[m] || 0, expenseByMonth[m] || 0)), 100);

  /* ── Frequency grouping ──────────────────────────────── */
  const freqGroups = (): { label: string; income: number; expense: number }[] => {
    if (freqView === 'monthly') {
      return allMonths.map(m => ({
        label:   new Date(m + '-01').toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
        income:  incomeByMonth[m]  || 0,
        expense: expenseByMonth[m] || 0,
      }));
    }
    if (freqView === 'quarterly') {
      return ['Q1 (Apr–Jun)', 'Q2 (Jul–Sep)', 'Q3 (Oct–Dec)', 'Q4 (Jan–Mar)'].map((label, qi) => {
        const qm = allMonths.slice(qi * 3, qi * 3 + 3);
        return { label, income: qm.reduce((s, m) => s + (incomeByMonth[m] || 0), 0), expense: qm.reduce((s, m) => s + (expenseByMonth[m] || 0), 0) };
      });
    }
    const inc = allMonths.reduce((s, m) => s + (incomeByMonth[m] || 0), 0);
    const exp = allMonths.reduce((s, m) => s + (expenseByMonth[m] || 0), 0);
    return [{ label: taxYearLabel(taxYear), income: inc, expense: exp }];
  };

  /* ── CSV download ────────────────────────────────────── */
  const downloadCSV = () => {
    if (!data) return;
    const rows: any[][] = [
      ['Type', 'Date', 'Property', 'Category', 'Description', 'Amount (£)'],
      ...data.income.map(t => ['Income', t.date.slice(0, 10), t.property_address, t.category, t.description, t.amount]),
      ...data.expenses.map(t => ['Expense', t.date.slice(0, 10), t.property_address, t.category, t.description, `-${t.amount}`]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url; a.download = `tax-report-${taxYear}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const TABS: { id: ActiveTab; label: string }[] = [
    { id: 'summary',    label: 'Summary' },
    { id: 'income',     label: 'Income' },
    { id: 'expenses',   label: 'Expenses' },
    { id: 'properties', label: 'Per Property' },
    { id: 'tax',        label: 'Tax Report' },
  ];

  return (
    <div className="money-page">
      <style>{MONEY_PAGE_CSS}{AC_EXTRA_CSS}</style>

      <div className="money-banner-wrap">
        <div className="money-banner-glass">
          <div>
            <h1><span className="money-icon">£</span>Accounting</h1>
            <p>{data
              ? `${taxYearLabel(taxYear)} · Net ${data.netIncome >= 0 ? '+' : ''}£${Math.abs(Math.round(data.netIncome)).toLocaleString()}`
              : 'Loading…'}</p>
          </div>
          <div className="money-banner-actions">
            <select className="money-input-dark" value={taxYear} onChange={e => setTaxYear(e.target.value)}>
              {TAX_YEARS.map(ty => <option key={ty} value={ty}>{taxYearLabel(ty)}</option>)}
            </select>
            <button type="button" className="money-btn-ghost" onClick={() => { setShowCsvImport(!showCsvImport); setActiveTab('expenses'); }}>Import CSV</button>
            <button type="button" className="money-btn-gold" onClick={() => { setShowAddExpense(!showAddExpense); setActiveTab('expenses'); }}>+ Add expense</button>
          </div>
        </div>
      </div>

      {showCsvImport && (
        <div className="ac-form" style={{ borderColor: '#0369a1' }}>
          <div className="ac-form-title" style={{ color: '#0369a1' }}>📥 Import Bank Statement CSV</div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>Export from Barclays, HSBC, Lloyds, NatWest, etc. Must include Date and Amount (or Debit/Credit) columns.</p>
          <div className="ac-grid">
            <div>
              <label className="field-label">Property *</label>
              <select value={csvProperty} onChange={e => setCsvProperty(e.target.value)} className="field-input">
                <option value="">— Select property —</option>
                {properties.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Upload file</label>
              <input ref={csvFileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = ev => setCsvText(String(ev.target?.result || '')); r.readAsText(f); } }} />
              <button className="btn-csv" onClick={() => csvFileRef.current?.click()}>Choose CSV file</button>
            </div>
            <div className="span-all">
              <label className="field-label">Or paste CSV</label>
              <textarea value={csvText} onChange={e => setCsvText(e.target.value)} rows={6} className="field-input" placeholder="Date,Description,Amount&#10;01/04/2025,Rent received,1200.00&#10;05/04/2025,Insurance,-450.00" style={{ fontFamily: 'monospace', fontSize: 12 }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={importBankCsv} disabled={importingCsv} className="btn-save" style={{ background: '#0369a1' }}>{importingCsv ? 'Importing…' : 'Import Transactions'}</button>
            <button onClick={() => { setShowCsvImport(false); setCsvText(''); setError(''); }} className="btn-cancel">Cancel</button>
          </div>
        </div>
      )}

      {data && (
        <div className="money-kpi-row" style={{ gridTemplateColumns: taxInfo ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)' }}>
          <div className="money-kpi money-kpi-green">
            <div className="kl">Gross income</div>
            <div className="kv" style={{ fontSize: data.totalIncome >= 100000 ? 18 : 26 }}>£{Math.round(data.totalIncome).toLocaleString()}</div>
            <div className="ks">{data.income.length} payment{data.income.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="money-kpi money-kpi-red">
            <div className="kl">Expenses</div>
            <div className="kv" style={{ fontSize: data.totalExpenses >= 100000 ? 18 : 26 }}>£{Math.round(data.totalExpenses).toLocaleString()}</div>
            <div className="ks">{data.expenses.length} item{data.expenses.length !== 1 ? 's' : ''}</div>
          </div>
          <div className="money-kpi money-kpi-black">
            <div className="kl">Net profit</div>
            <div className="kv">{data.netIncome >= 0 ? '+' : ''}£{Math.abs(Math.round(data.netIncome)).toLocaleString()}</div>
            <div className="ks">rental P&amp;L</div>
          </div>
          <div className="money-kpi money-kpi-white">
            <div className="kl">Properties</div>
            <div className="kv">{data.per_property.length}</div>
            <div className="ks">with transactions</div>
          </div>
          {taxInfo && (
            <div className="money-kpi money-kpi-gold">
              <div className="kl">Est. tax</div>
              <div className="kv" style={{ fontSize: taxInfo.total >= 100000 ? 18 : 26 }}>£{Math.round(taxInfo.total).toLocaleString()}</div>
              <div className="ks">HMRC estimate</div>
            </div>
          )}
        </div>
      )}

      {/* Expense form */}
      {showAddExpense && (
        <div className="ac-form">
          <div className="ac-form-title">{editingExpenseId ? 'Edit Expense' : 'Add Manual Expense'}</div>
          {error && <div className="toast-err" style={{ marginBottom: 12 }}>❌ {error}</div>}
          <div className="ac-grid">
            <div>
              <label className="field-label">Property *</label>
              <select value={expenseForm.property_address} onChange={e => setExpenseForm(p => ({ ...p, property_address: e.target.value }))} className="field-input">
                <option value="">Select property…</option>
                {properties.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Category</label>
              <select value={expenseForm.category} onChange={e => setExpenseForm(p => ({ ...p, category: e.target.value }))} className="field-input">
                {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Amount (£) *</label>
              <input type="number" min="0" step="0.01" value={expenseForm.amount} onChange={e => setExpenseForm(p => ({ ...p, amount: e.target.value }))} placeholder="250.00" className="field-input" />
            </div>
            <div>
              <label className="field-label">Date *</label>
              <input type="date" value={expenseForm.date} onChange={e => setExpenseForm(p => ({ ...p, date: e.target.value }))} className="field-input" />
            </div>
            {expenseForm.category === 'Mortgage Interest' && (
              <div>
                <label className="field-label">Interest Portion (£) — Section 24</label>
                <input type="number" min="0" step="0.01" value={expenseForm.mortgage_interest} onChange={e => setExpenseForm(p => ({ ...p, mortgage_interest: e.target.value, amount: e.target.value || p.amount }))} placeholder="e.g. 4500" className="field-input" />
              </div>
            )}
            <div className="span-all">
              <label className="field-label">Description *</label>
              <input value={expenseForm.description} onChange={e => setExpenseForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Annual insurance renewal, boiler repair…" className="field-input" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={addManualExpense} disabled={savingExpense} className="btn-save">{savingExpense ? 'Saving…' : 'Save Expense'}</button>
            <button onClick={() => { setShowAddExpense(false); setEditingExpenseId(null); setError(''); }} className="btn-cancel">Cancel</button>
          </div>
        </div>
      )}

      {/* Toasts */}
      {error   && !showAddExpense && <div className="toast-err">❌ {error}</div>}
      {success && <div className="toast-ok">✅ {success}</div>}

      <div className="money-tabs-dark">
        {TABS.map(t => (
          <button key={t.id} type="button" className={`money-tab${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8', fontSize: 15 }}>Loading…</div>}

      {!loading && data && (
        <>
          {/* ── SUMMARY ── */}
          {activeTab === 'summary' && (
            <div className="money-panel">
              <div className="money-panel-title">Monthly Income vs Expenses — {taxYearLabel(taxYear)}</div>
              <div className="ac-chart">
                {allMonths.map(m => {
                  const inc  = incomeByMonth[m]  || 0;
                  const exp  = expenseByMonth[m] || 0;
                  const incH = Math.round((inc / maxBar) * 96);
                  const expH = Math.round((exp / maxBar) * 96);
                  const lbl  = new Date(m + '-01').toLocaleDateString('en-GB', { month: 'short' });
                  return (
                    <div key={m} className="ac-col">
                      <div className="ac-bars">
                        <div className="ac-bar" title={`Income £${inc.toLocaleString()}`}  style={{ height: incH, background: MONEY.green }} />
                        <div className="ac-bar" title={`Expenses £${exp.toLocaleString()}`} style={{ height: expH, background: MONEY.red }} />
                      </div>
                      <div className="ac-lbl">{lbl}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 10, justifyContent: 'center' }}>
                {[[MONEY.green, 'Income'], [MONEY.red, 'Expenses']].map(([bg, lbl]) => (
                  <span key={lbl} style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ width: 12, height: 12, background: bg, borderRadius: 2, display: 'inline-block' }} />{lbl}
                  </span>
                ))}
              </div>
              {data.income.length === 0 && data.expenses.length === 0 && (
                <div className="ac-empty" style={{ marginTop: 24 }}>
                  <div className="ei">💰</div>
                  <h3>No transactions for {taxYearLabel(taxYear)}</h3>
                  <p>Transactions auto-record when rent is marked paid or maintenance jobs are completed.</p>
                </div>
              )}
              {(data.totalCapitalIncome || 0) > 0 || (data.totalCapitalExpenses || 0) > 0 ? (
                <div style={{ marginTop: 24, padding: 18, background: MONEY.cream, border: `2px solid ${MONEY.black}`, borderRadius: 12 }}>
                  <div style={{ fontWeight: 800, color: MONEY.black, marginBottom: 8 }}>Capital &amp; disposals (not rental profit)</div>
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>Property sales and acquisition/disposal costs from Sales & Buy — kept separate from your rental P&amp;L above.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                    <div><div style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>SALE PROCEEDS</div><div style={{ fontWeight: 800, color: '#16a34a' }}>{fmtGBP(data.totalCapitalIncome || 0)}</div></div>
                    <div><div style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>CAPITAL COSTS</div><div style={{ fontWeight: 800, color: '#dc2626' }}>{fmtGBP(data.totalCapitalExpenses || 0)}</div></div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* ── INCOME ── */}
          {activeTab === 'income' && (
            <div className="money-panel">
              <div className="money-panel-title">
                <span>💷 Rent Income — {taxYearLabel(taxYear)}</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, color: '#16a34a' }}>{fmtGBP(data.totalIncome)}</span>
              </div>
              {data.income.length > 0 && (
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Filter by property:</span>
                  <select value={incomeFilter} onChange={e => setIncomeFilter(e.target.value)} style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', cursor: 'pointer', background: '#fff' }}>
                    <option value="">All properties ({data.income.length} entries)</option>
                    {[...new Set(data.income.map(t => t.property_address))].sort().map(addr => (
                      <option key={addr} value={addr}>{addr} ({data.income.filter(t => t.property_address === addr).length})</option>
                    ))}
                  </select>
                  {incomeFilter && <button onClick={() => setIncomeFilter('')} style={{ padding: '6px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: '#fff', color: '#64748b' }}>✕ Clear</button>}
                </div>
              )}
              {data.income.length === 0
                ? <div className="ac-empty"><div className="ei">💷</div><h3>No income this year</h3><p>Income is auto-logged when rent is marked paid.</p></div>
                : <div style={{ overflowX: 'auto' }}>
                    <table className="money-table">
                      <thead><tr><th>Date</th><th>Property</th><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
                      <tbody>
                        {(incomeFilter ? data.income.filter(t => t.property_address === incomeFilter) : data.income).map(t => (
                          <tr key={t.id}>
                            <td style={{ color: '#64748b', whiteSpace: 'nowrap', fontFamily: "'DM Mono',monospace" }}>{fmtDate(t.date)}</td>
                            <td style={{ fontWeight: 700 }}>{t.property_address}</td>
                            <td>{t.description || `Rent — ${t.property_address}`}</td>
                            <td style={{ fontWeight: 800, color: '#16a34a', textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(t.amount))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              }
            </div>
          )}

          {/* ── EXPENSES ── */}
          {activeTab === 'expenses' && (
            <div className="money-panel">
              <div className="money-panel-title">
                <span>🔧 Expenses — {taxYearLabel(taxYear)}</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, color: '#dc2626' }}>{fmtGBP(data.totalExpenses)}</span>
              </div>
              {data.expenses.length === 0
                ? <div className="ac-empty"><div className="ei">🔧</div><h3>No expenses this year</h3><p>Use "+ Add Expense" or complete a maintenance job.</p></div>
                : <div style={{ overflowX: 'auto' }}>
                    <table className="money-table">
                      <thead><tr><th>Date</th><th>Property</th><th>Category</th><th>Description</th><th>Source</th><th style={{ textAlign: 'right' }}>Cost</th><th></th></tr></thead>
                      <tbody>
                        {data.expenses.map(t => (
                          <tr key={t.id}>
                            <td style={{ color: '#64748b', whiteSpace: 'nowrap', fontFamily: "'DM Mono',monospace" }}>{fmtDate(t.date)}</td>
                            <td style={{ fontWeight: 700 }}>{t.property_address}</td>
                            <td style={{ color: '#64748b' }}>{t.category}</td>
                            <td>{t.description || t.category}</td>
                            <td>
                              <span className="src-badge" style={{
                                background: t.reference_type === 'maintenance' ? '#dbeafe' : t.reference_type === 'inventory' ? '#fef3c7' : '#f1f5f9',
                                color:      t.reference_type === 'maintenance' ? '#1e40af' : t.reference_type === 'inventory' ? '#92400e' : '#64748b',
                              }}>
                                {t.reference_type === 'maintenance' ? '🔧 Maint.' : t.reference_type === 'inventory' ? '📦 Inv.' : '✏️ Manual'}
                              </span>
                            </td>
                            <td style={{ fontWeight: 800, color: '#dc2626', textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(t.amount))}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {t.reference_type === 'manual' && (
                                <>
                                  <button onClick={() => startEditExpense(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#1d4ed8', fontWeight: 700, marginRight: 6 }}>Edit</button>
                                  <button onClick={() => deleteExpense(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 700 }}>Del</button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              }
            </div>
          )}

          {/* ── PER PROPERTY ── */}
          {activeTab === 'properties' && (
            <div className="money-panel">
              <div className="money-panel-title">🏠 Per-Property Breakdown — {taxYearLabel(taxYear)}</div>
              {data.per_property.length === 0
                ? <div className="ac-empty"><div className="ei">🏠</div><h3>No property data yet</h3><p>Transactions appear once rent is paid.</p></div>
                : <div style={{ overflowX: 'auto' }}>
                    <table className="money-table">
                      <thead><tr><th>Property</th><th>Income</th><th>Expenses</th><th>Net Profit</th><th>Margin</th></tr></thead>
                      <tbody>
                        {[...data.per_property].sort((a, b) => b.net - a.net).map((p, i) => {
                          const margin = p.income > 0 ? Math.round((p.net / p.income) * 100) : 0;
                          return (
                            <tr key={i}>
                              <td style={{ fontWeight: 700 }}>{p.address}</td>
                              <td style={{ fontWeight: 700, color: '#16a34a', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(p.income))}</td>
                              <td style={{ fontWeight: 700, color: '#dc2626', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(p.expenses))}</td>
                              <td style={{ fontWeight: 800, color: p.net >= 0 ? '#0d9488' : '#dc2626', fontFamily: "'DM Mono',monospace" }}>{p.net >= 0 ? '+' : ''}{fmtGBP(Number(p.net))}</td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div className="prop-bar-bg" style={{ flex: 1 }}>
                                    <div className="prop-bar-fill" style={{ width: `${Math.max(0, Math.min(100, margin))}%`, background: margin >= 80 ? '#16a34a' : margin >= 50 ? '#0d9488' : margin >= 0 ? '#d97706' : '#dc2626' }} />
                                  </div>
                                  <span style={{ fontSize: 12, fontWeight: 700, minWidth: 36, color: margin >= 0 ? '#374151' : '#dc2626' }}>{margin}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>TOTAL</td>
                          <td style={{ color: '#16a34a', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(data.totalIncome))}</td>
                          <td style={{ color: '#dc2626', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(data.totalExpenses))}</td>
                          <td style={{ color: data.netIncome >= 0 ? '#0d9488' : '#dc2626', fontFamily: "'DM Mono',monospace" }}>{data.netIncome >= 0 ? '+' : ''}{fmtGBP(Number(data.netIncome))}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
              }
            </div>
          )}

          {/* ── TAX REPORT ── */}
          {activeTab === 'tax' && (
            <>
              {/* HMRC Summary */}
              <div className="money-panel">
                <div className="money-panel-title">
                  <span>📋 Tax Report — {taxYearLabel(taxYear)}</span>
                  <button className="btn-csv" onClick={downloadCSV}>⬇ Download CSV</button>
                </div>

                <div className="tax-section">
                  <div className="tax-section-title">Rental Income — HMRC SA105</div>
                  <div className="tax-row"><span className="tax-row-label">Gross Rental Income</span><span className="tax-row-value" style={{ color: '#16a34a' }}>{fmtGBP(data.totalIncome)}</span></div>
                  <div className="tax-row"><span className="tax-row-label">Less: Allowable Expenses</span><span className="tax-row-value" style={{ color: '#dc2626' }}>−{fmtGBP(data.totalExpenses)}</span></div>
                  <div className="tax-row total"><span className="tax-row-label">Net Rental Profit</span><span className="tax-row-value">{fmtGBP(data.netIncome)}</span></div>
                  {mortgageInterest > 0 && (
                    <>
                      <div className="tax-row"><span className="tax-row-label">Mortgage Interest (Section 24)</span><span className="tax-row-value" style={{ color: '#64748b' }}>{fmtGBP(mortgageInterest)}</span></div>
                      <div className="tax-row"><span className="tax-row-label">Section 24 Tax Credit (20%)</span><span className="tax-row-value" style={{ color: '#16a34a' }}>−{fmtGBP(section24Credit)}</span></div>
                      <div className="tax-row total"><span className="tax-row-label">Taxable Profit (after S24)</span><span className="tax-row-value">{fmtGBP(taxableProfit)}</span></div>
                    </>
                  )}
                </div>

                {taxInfo && (
                  <div className="tax-section">
                    <div className="tax-section-title">Estimated Income Tax ({taxYear.replace('-', '/20')} bands)</div>
                    <div className="tax-row"><span className="tax-row-label">Personal Allowance — up to £{PERSONAL_ALLOWANCE.toLocaleString()} (0%)</span><span className="tax-row-value" style={{ color: '#94a3b8' }}>—</span></div>
                    <div className="tax-row">
                      <span className="tax-row-label">Basic Rate — £12,571 to £50,270 at 20%</span>
                      <span className="tax-row-value" style={{ color: taxInfo.basic > 0 ? '#ea580c' : '#94a3b8' }}>{taxInfo.basic > 0 ? fmtGBP(taxInfo.basic) : '—'}</span>
                    </div>
                    <div className="tax-row">
                      <span className="tax-row-label">Higher Rate — £50,271 to £125,140 at 40%</span>
                      <span className="tax-row-value" style={{ color: taxInfo.higher > 0 ? '#dc2626' : '#94a3b8' }}>{taxInfo.higher > 0 ? fmtGBP(taxInfo.higher) : '—'}</span>
                    </div>
                    {taxInfo.additional > 0 && (
                      <div className="tax-row">
                        <span className="tax-row-label">Additional Rate — above £125,140 at 45%</span>
                        <span className="tax-row-value" style={{ color: '#b91c1c' }}>{fmtGBP(taxInfo.additional)}</span>
                      </div>
                    )}
                    <div className="tax-row total"><span className="tax-row-label">Estimated Total Tax Liability</span><span className="tax-row-value">£{Math.round(taxInfo.total).toLocaleString()}</span></div>
                  </div>
                )}

                <div className="tax-disclaimer">
                  ⚠️ <strong>Estimate only.</strong> This calculation uses UK Income Tax bands for {taxYear.replace('-', '/20')} assuming rental profit is your only income above the personal allowance. Mortgage interest qualifies for a 20% tax credit (Section 24), it is not a direct allowable expense deduction. National Insurance Class 2/4 may apply. Always use HMRC's official Self Assessment tools or consult a qualified accountant for your actual tax return.
                </div>
              </div>

              {/* Income Frequency */}
              <div className="money-panel">
                <div className="money-panel-title">
                  <span>📅 Income by Frequency</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['monthly', 'quarterly', 'annual'] as FreqView[]).map(f => (
                      <button key={f} className={`freq-btn${freqView === f ? ' active' : ''}`} onClick={() => setFreqView(f)}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="money-table">
                    <thead><tr>
                      <th>Period</th>
                      <th style={{ textAlign: 'right' }}>Rental Income</th>
                      <th style={{ textAlign: 'right' }}>Expenses</th>
                      <th style={{ textAlign: 'right' }}>Net</th>
                    </tr></thead>
                    <tbody>
                      {freqGroups().map((g, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 700 }}>{g.label}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a', fontFamily: "'DM Mono',monospace" }}>{g.income > 0 ? fmtGBP(g.income) : '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626', fontFamily: "'DM Mono',monospace" }}>{g.expense > 0 ? fmtGBP(g.expense) : '—'}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800, fontFamily: "'DM Mono',monospace", color: (g.income - g.expense) >= 0 ? '#0d9488' : '#dc2626' }}>
                            {(g.income > 0 || g.expense > 0) ? fmtGBP(g.income - g.expense) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Gross Yield per Property */}
              {data.per_property.length > 0 && (
                <div className="money-panel">
                  <div className="money-panel-title">🏠 Gross Rental Yield per Property</div>
                  <p style={{ fontSize: 13, color: '#94a3b8', margin: '0 0 16px' }}>Enter estimated property value to calculate gross yield (annual rent ÷ value × 100). Net yield deducts expenses.</p>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="money-table">
                      <thead><tr>
                        <th>Property</th>
                        <th style={{ textAlign: 'right' }}>Annual Income</th>
                        <th style={{ textAlign: 'right' }}>Est. Property Value (£)</th>
                        <th style={{ textAlign: 'right' }}>Gross Yield</th>
                        <th style={{ textAlign: 'right' }}>Net Yield</th>
                      </tr></thead>
                      <tbody>
                        {data.per_property.map((p, i) => {
                          const propVal    = parseFloat(yieldInputs[p.address] || '') || 0;
                          const grossYield = propVal > 0 ? (p.income   / propVal) * 100 : null;
                          const netYield   = propVal > 0 ? (p.net      / propVal) * 100 : null;
                          const gColor = grossYield === null ? '#94a3b8' : grossYield >= 8 ? '#16a34a' : grossYield >= 5 ? '#0d9488' : '#d97706';
                          const nColor = netYield   === null ? '#94a3b8' : netYield   >= 5 ? '#16a34a' : netYield   >= 2 ? '#0d9488' : netYield >= 0 ? '#d97706' : '#dc2626';
                          return (
                            <tr key={i}>
                              <td style={{ fontWeight: 700 }}>{p.address}</td>
                              <td style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a', fontFamily: "'DM Mono',monospace" }}>{fmtGBP(Number(p.income))}</td>
                              <td style={{ textAlign: 'right' }}>
                                <input type="number" className="yield-input" placeholder="e.g. 250000"
                                  value={yieldInputs[p.address] || ''}
                                  onChange={e => setYieldInputs(prev => ({ ...prev, [p.address]: e.target.value }))} />
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 800, color: gColor, fontFamily: "'DM Mono',monospace" }}>
                                {grossYield !== null ? `${grossYield.toFixed(2)}%` : '—'}
                              </td>
                              <td style={{ textAlign: 'right', fontWeight: 800, color: nColor, fontFamily: "'DM Mono',monospace" }}>
                                {netYield !== null ? `${netYield.toFixed(2)}%` : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
                    Yield guide: 🟡 5–7% average UK market · 🟢 8%+ strong yield · below 4% consider costs carefully
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
